import React, { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from 'react-i18next';

type ProductCategory = {
    _id: string;
    name: string;
    subcategories: { name: string; products: any[] }[];
    products: any[];
};

interface NavbarProps {
    products: ProductCategory[];
    lang: string;
}

const NavbarWithPushContent: React.FC<NavbarProps> = ({ products, lang }) => {
    const { t } = useTranslation();
    const [isWhoWeAreOpen, setIsWhoWeAreOpen] = useState(false);
    const [isProductsOpen, setIsProductsOpen] = useState(false);
    const location = useLocation();
    const currentPath = location.pathname;

    const handleWhoWeAreEnter = () => {
        setIsWhoWeAreOpen(true);
        setIsProductsOpen(false);
    };

    const handleProductsEnter = () => {
        setIsProductsOpen(true);
        setIsWhoWeAreOpen(false);
    };

    const handleMouseLeave = () => {
        setIsWhoWeAreOpen(false);
        setIsProductsOpen(false);
    };

    const handleLinkHover = () => {
        setIsWhoWeAreOpen(false);
        setIsProductsOpen(false);
    };

    const handleLinkClick = () => {
        setIsWhoWeAreOpen(false);
        setIsProductsOpen(false);
    };

    const nameToKeyMap: Record<string, string> = {
        "Medicinal Products": "medicinal",
        "CBD Products": "cbd",
        "Herbal Products": "herbal",
        "Dietary Supplements": "dietary",
        "Personal Care Products": "personalCare",
    };

    const order = ["medicinal", "cbd", "herbal", "dietary", "personalCare"];

    const sortedProducts = [...products].sort(
        (a, b) =>
            order.indexOf(nameToKeyMap[a.name]) -
            order.indexOf(nameToKeyMap[b.name])
    );

    const subcategoryTranslations: Record<string, string> = {
        "OTC": "ОТЦ",
        "Oral": "oral",
        "Topical": "topical",
        "Cosmetics": "cosmetics",
        "GALAHEALTH": "galahealth",
        "JEKOGAL": "jekogal",
        "GALAPRO": "galapro",
        "BABYGAL Baby Skin Care": "babyGalSkinCare",
        "SENSILIS MAM CARE": "sensilisMamCare",
        "FACE & LIP CARE": "face&Lip",
        "SENSILIS FOOT CARE": "sensilisFootCare",
        "SENSILIS NAIL & HAND CARE": "sensilisNail",
        "SENSILIS OILS": "sensilisOils",
        "SILK HAIR REMOVAL": "sensilisHair",
        "SOLARUS SUN PROTECTION CARE": "solarus",
        "CITROGALA INSPECT PROTECTION": "citrogala"
    };

    const orderSubcategory = ["otc", "oral", "topical", "cosmetics", "galahealth", "jekogal", "galapro", "babyGalSkinCare",
        "sensilisMamCare", "face&Lip", "sensilisFootCare", "sensilisNail", "sensilisOils", "sensilisHair", "solarus", "citrogala"];

    const isDropdownOpen = isWhoWeAreOpen || isProductsOpen;

    // Shared classes for the top-level nav links with an animated sage-green underline.
    const linkBase =
        "relative py-1 text-base font-semibold tracking-wide transition-colors duration-200 " +
        "after:absolute after:left-0 after:-bottom-0.5 after:h-0.5 after:w-0 after:bg-brand-400 " +
        "after:transition-all after:duration-300 hover:after:w-full";

    const linkColor = (active: boolean) =>
        active ? "text-brand-500 after:w-full" : "text-brand-gray hover:text-brand-500";

    return (
        <div
            className="relative z-[9999]"
            onMouseLeave={handleMouseLeave}
        >
            <nav className="fixed top-0 left-0 w-full h-20 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
                <div className="max-w-7xl mx-auto h-full flex items-center justify-between px-6">
                    {/* Logo */}
                    <Link to="/" onMouseEnter={handleLinkHover} className="flex-shrink-0">
                        {lang === 'mk' ? (
                            <img src="/LOGO MK POSLEDNO.png" alt="Galafarm Logo" className="h-12 w-auto" />
                        ) : (
                            <img src="/LOGO GB POSLEDNO.png" alt="Galafarm Logo" className="h-12 w-auto" />
                        )}
                    </Link>

                    {/* Nav links */}
                    <div className="flex items-center gap-9">
                        <Link
                            to="/"
                            className={`${linkBase} ${linkColor(currentPath === '/')}`}
                            onMouseEnter={handleLinkHover}
                        >
                            {t(`navbar.home`)}
                        </Link>

                        <button
                            type="button"
                            className={`${linkBase} ${linkColor(currentPath === '/about' || currentPath === '/history')} flex items-center gap-1.5`}
                            onMouseEnter={handleWhoWeAreEnter}
                        >
                            {t(`navbar.whoWeAre`)}
                            <FaChevronDown
                                className={`text-xs transition-transform duration-300 ${isWhoWeAreOpen ? 'rotate-180' : ''}`}
                            />
                        </button>

                        <button
                            type="button"
                            className={`${linkBase} ${linkColor(currentPath.startsWith('/product'))} flex items-center gap-1.5`}
                            onMouseEnter={handleProductsEnter}
                        >
                            {t(`navbar.products`)}
                            <FaChevronDown
                                className={`text-xs transition-transform duration-300 ${isProductsOpen ? 'rotate-180' : ''}`}
                            />
                        </button>

                        <Link
                            to="/news"
                            className={`${linkBase} ${linkColor(currentPath.startsWith('/news'))}`}
                            onMouseEnter={handleLinkHover}
                        >
                            {t(`navbar.news`)}
                        </Link>

                        <Link
                            to="/blog"
                            className={`${linkBase} ${linkColor(currentPath.startsWith('/blog'))}`}
                            onMouseEnter={handleLinkHover}
                        >
                            {t(`navbar.health`)}
                        </Link>

                        <Link
                            to="/careers"
                            className={`${linkBase} ${linkColor(currentPath === '/careers')}`}
                            onMouseEnter={handleLinkHover}
                        >
                            {t(`navbar.careers`)}
                        </Link>

                        <Link
                            to="/contacts"
                            className={`${linkBase} ${linkColor(currentPath === '/contacts')}`}
                            onMouseEnter={handleLinkHover}
                        >
                            {t(`navbar.contacts`)}
                        </Link>

                        <div className="pl-2 border-l border-gray-200">
                            <LanguageSelector />
                        </div>
                    </div>
                </div>
            </nav>

            {/* Dropdown for Who We Are */}
            {isWhoWeAreOpen && (
                <div
                    className="fixed top-20 left-0 w-full z-40 bg-white border-b border-gray-100 shadow-lg animate-fade-slide-down"
                    onMouseEnter={handleWhoWeAreEnter}
                >
                    <div className="max-w-7xl mx-auto flex justify-center py-6">
                        <ul className="flex gap-6 text-brand-gray">
                            <li>
                                <Link
                                    to="/about"
                                    className={`block px-6 py-2.5 rounded-lg text-base font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600 ${currentPath === '/about' ? 'text-brand-500 bg-brand-50' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    {t(`navbar.aboutUs`)}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/history"
                                    className={`block px-6 py-2.5 rounded-lg text-base font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600 ${currentPath === '/history' ? 'text-brand-500 bg-brand-50' : ''}`}
                                    onClick={handleLinkClick}
                                >
                                    {t(`navbar.history`)}
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            )}

            {/* Dropdown for Products */}
            {isProductsOpen && (
                <div
                    className="fixed top-20 left-0 w-full z-40 bg-white border-b border-gray-100 shadow-lg animate-fade-slide-down max-h-[75vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
                    onMouseEnter={handleProductsEnter}
                >
                    <div className="max-w-7xl mx-auto flex justify-center items-start gap-10 py-8 px-6">
                        {sortedProducts.map((category, index) => (
                            <div key={index} className="min-w-[150px]">
                                <p className="px-2 pb-2 mb-2 border-b-2 border-brand-200 text-base font-bold text-brand-600 uppercase tracking-wide">
                                    {t(`products.${nameToKeyMap[category.name]}`)}
                                </p>

                                <ul className="space-y-1">
                                    {category.subcategories
                                        .sort((a, b) => {
                                            const keyA = subcategoryTranslations[a.name];
                                            const keyB = subcategoryTranslations[b.name];
                                            return orderSubcategory.indexOf(keyA) - orderSubcategory.indexOf(keyB);
                                        })
                                        .map((subcategory) => (
                                            <li key={subcategory.name}>
                                                {subcategory.name === "OTC" || subcategory.name === "ОТЦ" ? null : (
                                                    <p className="px-2 pt-2 text-sm font-bold text-brand-gray">
                                                        {t(`subcategories.${subcategory.name}`)}
                                                    </p>
                                                )}

                                                <ul>
                                                    {subcategory.products.map((product) => (
                                                        <li key={product._id}>
                                                            <Link
                                                                to={`/product/${product._id}`}
                                                                className={`block px-2 py-1 rounded text-sm transition-colors duration-150 hover:text-brand-600 hover:bg-brand-50 ${currentPath === `/product/${product._id}` ? 'text-brand-500 font-semibold' : 'text-gray-600'}`}
                                                                onClick={handleLinkClick}
                                                            >
                                                                <span
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: (product.name?.[lang] || "").replace(
                                                                            /®/g,
                                                                            '<sup style="font-size:10px;">®</sup>'
                                                                        ),
                                                                    }}
                                                                />
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </li>
                                        ))}
                                </ul>

                                <ul>
                                    {category.products.map((product) => (
                                        <li key={product._id}>
                                            <Link
                                                to={`/product/${product._id}`}
                                                className={`block px-2 py-1 rounded text-sm transition-colors duration-150 hover:text-brand-600 hover:bg-brand-50 ${currentPath === `/product/${product._id}` ? 'text-brand-500 font-semibold' : 'text-gray-600'}`}
                                                onClick={handleLinkClick}
                                            >
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: (product.name?.[lang] || "").replace(
                                                            /®/g,
                                                            '<sup style="font-size:10px;">®</sup>'
                                                        ),
                                                    }}
                                                />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Soft backdrop behind open dropdowns for focus */}
            {isDropdownOpen && (
                <div className="fixed inset-0 top-20 bg-black/10 z-30 animate-fade-in" />
            )}
        </div>
    );
};

export default NavbarWithPushContent;
